import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import UpdateIcon from '@mui/icons-material/Update';
import WarningIcon from '@mui/icons-material/Warning';
import BreadCrumbs from '../components/Breadcrumbs';
import { useSnackbar } from '../components/snackbar/SnackbarProvider';
import {
    deletePlugin,
    deleteServerPluginSchedule,
    enablePlugin,
    getPlugins,
    getServerPluginSchedules,
    indexServer,
    installPlugin,
    pinnedPlugin,
    saveServerPluginSchedule,
    updatePlugin,
} from '../http/ServerAPI';
import { indexPlugins } from '../http/PluginAPI';

const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
};

const defaultScheduleDraft = {
    schedule_id: null,
    enabled: true,
    action: 'disable',
    mode: 'before_wipe',
    offset_days: 2,
    run_at: '04:00',
    weekday: '1',
    starts_at: '',
    ends_at: '',
    cron: '',
    note: '',
};

const getFirstLetter = (name) => {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed[0].toUpperCase() : '#';
};

const hasPluginError = (plugin) => Boolean((plugin?.error || '').trim());

const getScheduleKey = (plugin) => String(plugin?.plugin_id ?? plugin?.name ?? plugin?.plugin_name ?? '');

const normalizeSchedules = (response) => {
    const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
            ? response
            : [];

    return list.reduce((acc, schedule) => {
        const key = String(schedule.plugin_id ?? schedule.plugin_name ?? '');
        if (!key) return acc;

        if (!acc[key]) acc[key] = [];

        acc[key].push({
            ...defaultScheduleDraft,
            ...schedule,
            enabled: schedule.enabled !== false && schedule.enabled !== 0,
            offset_days: schedule.offset_days ?? defaultScheduleDraft.offset_days,
            run_at: schedule.run_at || defaultScheduleDraft.run_at,
            weekday: String(schedule.weekday ?? defaultScheduleDraft.weekday),
        });

        acc[key].sort((a, b) => {
            if (a.enabled !== b.enabled) return Number(b.enabled) - Number(a.enabled);
            return String(a.next_run_at || '').localeCompare(String(b.next_run_at || ''));
        });

        return acc;
    }, {});
};

const getScheduleTitle = (schedule) => {
    if (!schedule) return 'Расписание не настроено';

    const actionLabel = {
        enable: 'включить',
        disable: 'отключить',
        update: 'обновить',
    }[schedule.action] || 'выполнить действие';

    if (schedule.mode === 'before_wipe') {
        return `${actionLabel} за ${schedule.offset_days || 0} дн. до вайпа`;
    }

    if (schedule.mode === 'after_wipe') {
        return `${actionLabel} после вайпа`;
    }

    if (schedule.mode === 'daily') {
        return `${actionLabel} ежедневно в ${schedule.run_at || '—'}`;
    }

    if (schedule.mode === 'weekly') {
        return `${actionLabel} еженедельно в ${schedule.run_at || '—'}`;
    }

    if (schedule.mode === 'once') {
        return `${actionLabel} один раз: ${schedule.starts_at || 'дата не задана'}`;
    }

    if (schedule.mode === 'cron') {
        return `${actionLabel} по cron`;
    }

    return 'Расписание настроено';
};

const ScheduleSummary = ({ schedules }) => {
    if (!schedules?.length) return null;

    const activeCount = schedules.filter(schedule => schedule.enabled).length;

    return (
        <Alert
            severity={activeCount ? 'info' : 'warning'}
            icon={<ScheduleIcon fontSize="small" />}
            sx={{ py: 0.5 }}
        >
            <Stack spacing={0.75}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Правил расписания: {schedules.length} · активных: {activeCount}
                </Typography>
                {schedules.slice(0, 3).map((schedule, index) => (
                    <Typography key={schedule.schedule_id || index} variant="caption" color="text.secondary">
                        {schedule.enabled ? '●' : '○'} {getScheduleTitle(schedule)}
                        {schedule.next_run_at ? ` · следующий: ${schedule.next_run_at}` : ''}
                    </Typography>
                ))}
                {schedules.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                        ещё {schedules.length - 3} правил…
                    </Typography>
                )}
            </Stack>
        </Alert>
    );
};

const ServerPlugins = observer(() => {
    const { id } = useParams();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const letterRefs = useRef({});

    const [filter, setFilter] = useState('');
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);
    const [serverPlugins, setServerPlugins] = useState([]);
    const [indexPluginsList, setIndexPluginsList] = useState([]);
    const [infoIndexServer, setInfoIndexServer] = useState({});
    const [scheduledPlugins, setScheduledPlugins] = useState({});
    const [confirmAction, setConfirmAction] = useState(null);
    const [scheduleDialog, setScheduleDialog] = useState({ open: false, plugin: null, draft: defaultScheduleDraft, editingScheduleId: null });
    const [loadingServerPlugins, setLoadingServerPlugins] = useState(true);
    const [loadingIndexPlugins, setLoadingIndexPlugins] = useState(true);
    const [loadingIndexServer, setLoadingIndexServer] = useState(true);
    const [loadingSchedules, setLoadingSchedules] = useState(true);
    const [savingSchedule, setSavingSchedule] = useState(false);

    const hrefs = [{ href: '/servers', name: 'Список серверов' }];

    const loadServerPlugins = async () => {
        setLoadingServerPlugins(true);
        try {
            const data = await getPlugins(id);
            setServerPlugins(Array.isArray(data) ? data : []);
        } catch (e) {
            snackbar.error('Не удалось загрузить плагины сервера');
        } finally {
            setLoadingServerPlugins(false);
        }
    };

    const loadSchedules = async () => {
        setLoadingSchedules(true);
        try {
            const data = await getServerPluginSchedules(id);
            setScheduledPlugins(normalizeSchedules(data));
        } catch (e) {
            setScheduledPlugins({});
            snackbar.warning('Расписание плагинов пока недоступно или endpoint ещё не реализован');
        } finally {
            setLoadingSchedules(false);
        }
    };

    useEffect(() => {
        loadServerPlugins();

        setLoadingIndexPlugins(true);
        indexPlugins()
            .then((data) => setIndexPluginsList(Array.isArray(data) ? data : []))
            .catch(() => snackbar.error('Не удалось загрузить каталог плагинов'))
            .finally(() => setLoadingIndexPlugins(false));

        setLoadingIndexServer(true);
        indexServer(id)
            .then((data) => setInfoIndexServer(data || {}))
            .catch(() => snackbar.error('Не удалось загрузить информацию о сервере'))
            .finally(() => setLoadingIndexServer(false));

        loadSchedules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const installedPluginNames = useMemo(
        () => new Set(serverPlugins.map(plugin => (plugin.name || '').toLowerCase())),
        [serverPlugins]
    );

    const serverErrorCount = useMemo(
        () => serverPlugins.filter(hasPluginError).length,
        [serverPlugins]
    );

    const scheduleCount = useMemo(
        () => Object.values(scheduledPlugins).reduce((total, rules) => total + (Array.isArray(rules) ? rules.length : 0), 0),
        [scheduledPlugins]
    );

    const filteredServerPlugins = useMemo(() => {
        const normalizedFilter = filter.trim().toLowerCase();

        return serverPlugins
            .filter(plugin => (plugin.name || '').toLowerCase().includes(normalizedFilter))
            .filter(plugin => !showErrorsOnly || hasPluginError(plugin))
            .sort((a, b) => {
                if (b.pinned === a.pinned) return (a.name || '').localeCompare(b.name || '');
                return (b.pinned === true) - (a.pinned === true);
            });
    }, [filter, serverPlugins, showErrorsOnly]);

    const filteredIndexPlugins = useMemo(() => {
        const normalizedFilter = filter.trim().toLowerCase();

        if (showErrorsOnly) return [];

        return indexPluginsList
            .filter(plugin => !installedPluginNames.has((plugin.plugin_name || '').toLowerCase()))
            .filter(plugin => (plugin.plugin_name || '').toLowerCase().includes(normalizedFilter))
            .sort((a, b) => (a.plugin_name || '').localeCompare(b.plugin_name || ''));
    }, [filter, indexPluginsList, installedPluginNames, showErrorsOnly]);

    const alphabetLetters = useMemo(() => {
        const letters = new Set();

        filteredServerPlugins.forEach(plugin => letters.add(getFirstLetter(plugin.name)));
        filteredIndexPlugins.forEach(plugin => letters.add(getFirstLetter(plugin.plugin_name)));

        return Array.from(letters).sort((a, b) => a.localeCompare(b));
    }, [filteredServerPlugins, filteredIndexPlugins]);

    const scrollToLetter = (letter) => {
        const target = letterRefs.current[`server-${letter}`] || letterRefs.current[`index-${letter}`];
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const resetFilters = () => {
        setFilter('');
        setShowErrorsOnly(false);
    };

    const confirm = (action, plugin, message) => {
        setConfirmAction({ action, plugin, message });
    };

    const executeConfirmAction = () => {
        if (confirmAction?.action && confirmAction?.plugin) {
            confirmAction.action(confirmAction.plugin);
        }
        setConfirmAction(null);
    };

    const handleEnable = async (plugin, status) => {
        setServerPlugins(prev => prev.map(item => item.plugin_id === plugin.plugin_id ? { ...item, enabled: status } : item));
        try {
            await enablePlugin(id, plugin.plugin_id, status);
            snackbar.success(status ? 'Плагин включён' : 'Плагин отключён');
        } catch (e) {
            snackbar.error('Не удалось изменить состояние плагина');
            loadServerPlugins();
        }
    };

    const handlePinned = async (plugin, status) => {
        setServerPlugins(prev => prev.map(item => item.plugin_id === plugin.plugin_id ? { ...item, pinned: status } : item));
        try {
            await pinnedPlugin(id, plugin.plugin_id, status);
        } catch (e) {
            snackbar.error('Не удалось закрепить плагин');
            loadServerPlugins();
        }
    };

    const handleInstall = (plugin) => {
        confirm(async () => {
            try {
                await installPlugin(id, plugin.plugin_id);
                snackbar.success(`Плагин ${plugin.plugin_name} установлен`);
                loadServerPlugins();
            } catch (e) {
                snackbar.error('Не удалось установить плагин');
            }
        }, plugin, `Установить плагин ${plugin.plugin_name}?`);
    };

    const handleRemove = (plugin) => {
        confirm(async () => {
            try {
                await deletePlugin(id, plugin.plugin_id);
                snackbar.success(`Плагин ${plugin.name} удалён`);
                loadServerPlugins();
                loadSchedules();
            } catch (e) {
                snackbar.error('Не удалось удалить плагин');
            }
        }, plugin, `Удалить плагин ${plugin.name}?`);
    };

    const handleUpdate = (plugin) => {
        confirm(async () => {
            try {
                await updatePlugin(id, plugin.plugin_id);
                snackbar.success(`Плагин ${plugin.name} отправлен на обновление`);
                loadServerPlugins();
            } catch (e) {
                snackbar.error('Не удалось обновить плагин');
            }
        }, plugin, `Обновить плагин ${plugin.name}?`);
    };

    const openScheduleDialog = (plugin) => {
        setScheduleDialog({
            open: true,
            plugin,
            draft: {
                ...defaultScheduleDraft,
                plugin_id: plugin.plugin_id,
                plugin_name: plugin.name,
            },
            editingScheduleId: null,
        });
    };

    const startNewScheduleRule = () => {
        const plugin = scheduleDialog.plugin;
        setScheduleDialog(prev => ({
            ...prev,
            draft: {
                ...defaultScheduleDraft,
                plugin_id: plugin?.plugin_id,
                plugin_name: plugin?.name,
            },
            editingScheduleId: null,
        }));
    };

    const editScheduleRule = (rule) => {
        setScheduleDialog(prev => ({
            ...prev,
            draft: {
                ...defaultScheduleDraft,
                ...rule,
                weekday: String(rule.weekday ?? defaultScheduleDraft.weekday),
            },
            editingScheduleId: rule.schedule_id ?? null,
        }));
    };

    const updateScheduleDraft = (field, value) => {
        setScheduleDialog(prev => ({
            ...prev,
            draft: {
                ...prev.draft,
                [field]: value,
            },
        }));
    };

    const closeScheduleDialog = () => {
        setScheduleDialog({ open: false, plugin: null, draft: defaultScheduleDraft, editingScheduleId: null });
    };

    const saveSchedule = async () => {
        if (!scheduleDialog.plugin) return;

        const plugin = scheduleDialog.plugin;
        const payload = {
            ...scheduleDialog.draft,
            schedule_id: scheduleDialog.editingScheduleId,
            server_id: Number(id),
            plugin_id: plugin.plugin_id,
            plugin_name: plugin.name,
        };

        setSavingSchedule(true);
        try {
            const response = await saveServerPluginSchedule(id, plugin.plugin_id, payload);
            const saved = response?.data || payload;
            const normalizedRule = {
                ...payload,
                ...saved,
                enabled: saved.enabled !== false && saved.enabled !== 0,
                weekday: String(saved.weekday ?? payload.weekday),
            };

            setScheduledPlugins(prev => {
                const key = String(plugin.plugin_id);
                const currentRules = Array.isArray(prev[key]) ? prev[key] : [];
                const scheduleId = normalizedRule.schedule_id;
                const exists = scheduleId && currentRules.some(rule => rule.schedule_id === scheduleId);
                const nextRules = exists
                    ? currentRules.map(rule => rule.schedule_id === scheduleId ? normalizedRule : rule)
                    : [...currentRules, normalizedRule];

                return {
                    ...prev,
                    [key]: nextRules,
                };
            });

            snackbar.success(scheduleDialog.editingScheduleId ? 'Правило расписания обновлено' : 'Правило расписания добавлено');
            startNewScheduleRule();
        } catch (e) {
            snackbar.error('Не удалось сохранить правило расписания');
        } finally {
            setSavingSchedule(false);
        }
    };

    const removeScheduleRule = async (rule) => {
        if (!scheduleDialog.plugin || !rule) return;

        const plugin = scheduleDialog.plugin;
        setSavingSchedule(true);
        try {
            await deleteServerPluginSchedule(id, plugin.plugin_id, rule.schedule_id);
            setScheduledPlugins(prev => {
                const key = String(plugin.plugin_id);
                const nextRules = (prev[key] || []).filter(item => item.schedule_id !== rule.schedule_id);
                const next = { ...prev };

                if (nextRules.length) next[key] = nextRules;
                else delete next[key];

                return next;
            });

            if (scheduleDialog.editingScheduleId === rule.schedule_id) {
                startNewScheduleRule();
            }

            snackbar.success('Правило расписания удалено');
        } catch (e) {
            snackbar.error('Не удалось удалить правило расписания');
        } finally {
            setSavingSchedule(false);
        }
    };

    const renderSkeletons = (count = 5) => Array.from({ length: count }).map((_, i) => (
        <motion.div key={`skeleton-${i}`} layout variants={fadeIn} initial="hidden" animate="visible" exit="exit">
            <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
            </Paper>
        </motion.div>
    ));

    const renderPluginCard = (plugin, source = 'server') => {
        const match = source === 'server'
            ? indexPluginsList.find(p => (p.plugin_name || '').toLowerCase() === (plugin.name || '').toLowerCase())
            : null;
        const versionMismatch = source === 'server' && match && match.version !== plugin.version;
        const pluginName = source === 'server' ? plugin.name : plugin.plugin_name;
        const pluginVersion = plugin.version;
        const pluginDate = plugin.date;
        const schedules = source === 'server' ? (scheduledPlugins[getScheduleKey(plugin)] || []) : [];
        const activeSchedules = schedules.filter(schedule => schedule.enabled);
        const hasSchedules = schedules.length > 0;

        return (
            <motion.div key={`${source}-${pluginName}`} layout variants={fadeIn} initial="hidden" animate="visible" exit="exit">
                <Paper
                    sx={{
                        p: 2,
                        mb: 2,
                        border: plugin.error ? '1px solid #f44336' : hasSchedules ? '1px solid rgba(25, 118, 210, 0.35)' : 'none',
                    }}
                    elevation={2}
                >
                    <Stack spacing={1.25}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {pluginName}
                                    </Typography>
                                    {hasSchedules && (
                                        <Chip
                                            size="small"
                                            color={activeSchedules.length ? 'primary' : 'default'}
                                            icon={<ScheduleIcon />}
                                            label={`правил: ${schedules.length}`}
                                        />
                                    )}
                                    {hasPluginError(plugin) && <Chip size="small" color="error" label="ошибка" />}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Версия: {pluginVersion || '—'}
                                    {versionMismatch && match && (
                                        <> · доступно обновление до {match.version}</>
                                    )}
                                    {pluginDate ? ` · ${pluginDate}` : ''}
                                </Typography>
                            </Box>

                            {source === 'server' && (
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Закрепить">
                                        <IconButton size="small" onClick={() => handlePinned(plugin, !plugin.pinned)}>
                                            <PushPinIcon color={plugin.pinned ? 'primary' : 'disabled'} fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Расписание действий">
                                        <IconButton size="small" onClick={() => openScheduleDialog(plugin)}>
                                            <ScheduleIcon color={hasSchedules ? 'primary' : 'disabled'} fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Stack>

                        {source === 'server' && <ScheduleSummary schedules={schedules} />}

                        {source === 'server' && (
                            <Typography variant="caption">
                                Конфиг плагина:
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/server/plugins/${id}/edit/0/${plugin.plugin_id}`)}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Typography>
                        )}

                        {plugin.error && (
                            <Box sx={{ bgcolor: '#ffebee', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                    Ошибка при запуске плагина
                                </Typography>
                                <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {plugin.error.length > 250 ? `${plugin.error.slice(0, 250)}...` : plugin.error}
                                </Typography>
                            </Box>
                        )}

                        {source === 'server' ? (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Switch checked={Boolean(plugin.enabled)} onChange={() => handleEnable(plugin, !plugin.enabled)} />
                                {versionMismatch && (
                                    <Button size="small" startIcon={<UpdateIcon />} onClick={() => handleUpdate(plugin)}>Обновить</Button>
                                )}
                                <Button size="small" variant="outlined" startIcon={<ScheduleIcon />} onClick={() => openScheduleDialog(plugin)}>
                                    Расписание
                                </Button>
                                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleRemove(plugin)}>Удалить</Button>
                            </Stack>
                        ) : (
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleInstall(plugin)}>Установить</Button>
                        )}
                    </Stack>
                </Paper>
            </motion.div>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', mt: 3, mb: 3 }}>
                <BreadCrumbs hrefs={hrefs} name={`Управление плагинами сервера ${infoIndexServer.title || ''}`} />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>Управление плагинами сервера {infoIndexServer.title || ''}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Плагины можно быстро найти, отфильтровать ошибки и настроить автоматические действия по расписанию.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={`Ошибки: ${serverErrorCount}`} color={serverErrorCount ? 'error' : 'default'} variant={serverErrorCount ? 'filled' : 'outlined'} />
                    <Chip label={`Расписаний: ${scheduleCount}`} color={scheduleCount ? 'primary' : 'default'} variant="outlined" />
                </Stack>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <Paper
                elevation={2}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 3,
                    position: 'sticky',
                    top: 12,
                    zIndex: 5,
                    backdropFilter: 'blur(8px)',
                    bgcolor: 'background.paper',
                }}
            >
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <TextField
                            label="Фильтр по имени плагина"
                            variant="outlined"
                            fullWidth
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Например: Skin, Stack, Zone..."
                        />

                        <Badge badgeContent={serverErrorCount} color="error" overlap="circular">
                            <Button
                                variant={showErrorsOnly ? 'contained' : 'outlined'}
                                color={showErrorsOnly ? 'error' : 'inherit'}
                                startIcon={<ErrorOutlineIcon />}
                                onClick={() => setShowErrorsOnly(prev => !prev)}
                                sx={{ minWidth: { xs: '100%', md: 220 } }}
                            >
                                С ошибками
                            </Button>
                        </Badge>

                        <Button
                            variant="text"
                            startIcon={<RestartAltIcon />}
                            onClick={resetFilters}
                            sx={{ minWidth: { xs: '100%', md: 150 } }}
                        >
                            Сбросить
                        </Button>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Алфавит:</Typography>
                        {alphabetLetters.length > 0 ? alphabetLetters.map(letter => (
                            <Chip key={letter} label={letter} size="small" clickable onClick={() => scrollToLetter(letter)} sx={{ fontWeight: 700 }} />
                        )) : (
                            <Typography variant="caption" color="text.secondary">нет подходящих плагинов</Typography>
                        )}
                    </Stack>

                    {showErrorsOnly && (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                            Показаны только установленные плагины, у которых есть текст ошибки.
                        </Alert>
                    )}

                    {loadingSchedules && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                            Загружаем расписания плагинов...
                        </Alert>
                    )}
                </Stack>
            </Paper>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Плагины на сервере</Typography>
                    <AnimatePresence>
                        {loadingServerPlugins
                            ? renderSkeletons()
                            : filteredServerPlugins.length > 0
                                ? filteredServerPlugins.map((p, index, list) => {
                                    const letter = getFirstLetter(p.name);
                                    const prevLetter = index > 0 ? getFirstLetter(list[index - 1].name) : null;
                                    return (
                                        <React.Fragment key={p.name}>
                                            {letter !== prevLetter && (
                                                <Box
                                                    ref={(node) => { if (node) letterRefs.current[`server-${letter}`] = node; }}
                                                    sx={{ scrollMarginTop: 150, mb: 1, mt: index === 0 ? 0 : 3 }}
                                                >
                                                    <Chip label={letter} size="small" color="primary" sx={{ fontWeight: 700 }} />
                                                </Box>
                                            )}
                                            {renderPluginCard(p, 'server')}
                                        </React.Fragment>
                                    );
                                })
                                : (
                                    <Paper sx={{ p: 3, textAlign: 'center' }} elevation={0}>
                                        <Typography color="text.secondary">
                                            {showErrorsOnly ? 'Плагинов с ошибками не найдено.' : 'Плагины не найдены.'}
                                        </Typography>
                                    </Paper>
                                )}
                    </AnimatePresence>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Доступна установка</Typography>
                    <AnimatePresence>
                        {showErrorsOnly ? (
                            <Paper sx={{ p: 3, textAlign: 'center' }} elevation={0}>
                                <Typography color="text.secondary">Фильтр ошибок применяется только к установленным плагинам.</Typography>
                            </Paper>
                        ) : loadingIndexPlugins
                            ? renderSkeletons()
                            : filteredIndexPlugins.length > 0
                                ? filteredIndexPlugins.map((p, index, list) => {
                                    const letter = getFirstLetter(p.plugin_name);
                                    const prevLetter = index > 0 ? getFirstLetter(list[index - 1].plugin_name) : null;
                                    return (
                                        <React.Fragment key={p.plugin_name}>
                                            {letter !== prevLetter && (
                                                <Box
                                                    ref={(node) => { if (node) letterRefs.current[`index-${letter}`] = node; }}
                                                    sx={{ scrollMarginTop: 150, mb: 1, mt: index === 0 ? 0 : 3 }}
                                                >
                                                    <Chip label={letter} size="small" color="secondary" sx={{ fontWeight: 700 }} />
                                                </Box>
                                            )}
                                            {renderPluginCard(p, 'index')}
                                        </React.Fragment>
                                    );
                                })
                                : (
                                    <Paper sx={{ p: 3, textAlign: 'center' }} elevation={0}>
                                        <Typography color="text.secondary">Доступные для установки плагины не найдены.</Typography>
                                    </Paper>
                                )}
                    </AnimatePresence>
                </Grid>
            </Grid>

            <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmAction?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction(null)}>Отмена</Button>
                    <Button onClick={executeConfirmAction} autoFocus>Подтвердить</Button>
                </DialogActions>
            </Dialog>

            <Dialog maxWidth="md" fullWidth open={scheduleDialog.open} onClose={closeScheduleDialog}>
                <DialogTitle>
                    Расписание: {scheduleDialog.plugin?.name || ''}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Теперь у одного плагина может быть несколько правил. Например: отключить за 2 дня до вайпа и включить сразу после вайпа.
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">Текущие правила</Typography>
                                <Button size="small" startIcon={<AddIcon />} onClick={startNewScheduleRule} disabled={savingSchedule}>
                                    Новое правило
                                </Button>
                            </Stack>

                            <Stack spacing={1}>
                                {(scheduledPlugins[String(scheduleDialog.plugin?.plugin_id)] || []).length ? (
                                    (scheduledPlugins[String(scheduleDialog.plugin?.plugin_id)] || []).map((rule) => (
                                        <Paper
                                            key={rule.schedule_id || `${rule.action}-${rule.mode}-${rule.run_at}`}
                                            variant="outlined"
                                            sx={{
                                                p: 1.25,
                                                borderColor: scheduleDialog.editingScheduleId === rule.schedule_id ? 'primary.main' : 'divider',
                                                bgcolor: scheduleDialog.editingScheduleId === rule.schedule_id ? 'action.hover' : 'background.paper',
                                            }}
                                        >
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {rule.enabled ? '●' : '○'} {getScheduleTitle(rule)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {rule.next_run_at ? `Следующий запуск: ${rule.next_run_at}` : 'Следующий запуск не рассчитан'}
                                                        {rule.last_run_at ? ` · последний: ${rule.last_run_at}` : ''}
                                                        {rule.note ? ` · ${rule.note}` : ''}
                                                    </Typography>
                                                    {rule.last_error && (
                                                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                                            Ошибка: {rule.last_error}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton size="small" onClick={() => editScheduleRule(rule)} disabled={savingSchedule}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => removeScheduleRule(rule)} disabled={savingSchedule}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Для этого плагина пока нет правил расписания.
                                    </Typography>
                                )}
                            </Stack>
                        </Paper>

                        <Divider />

                        <Typography variant="subtitle2">
                            {scheduleDialog.editingScheduleId ? 'Редактирование правила' : 'Новое правило'}
                        </Typography>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2">Правило активно</Typography>
                            <Switch
                                checked={Boolean(scheduleDialog.draft.enabled)}
                                onChange={(e) => updateScheduleDraft('enabled', e.target.checked)}
                            />
                        </Stack>

                        <FormControl fullWidth>
                            <InputLabel>Действие</InputLabel>
                            <Select
                                label="Действие"
                                value={scheduleDialog.draft.action}
                                onChange={(e) => updateScheduleDraft('action', e.target.value)}
                            >
                                <MenuItem value="disable">Отключить плагин</MenuItem>
                                <MenuItem value="enable">Включить плагин</MenuItem>
                                <MenuItem value="update">Обновить плагин</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Когда выполнять</InputLabel>
                            <Select
                                label="Когда выполнять"
                                value={scheduleDialog.draft.mode}
                                onChange={(e) => updateScheduleDraft('mode', e.target.value)}
                            >
                                <MenuItem value="before_wipe">За N дней до вайпа</MenuItem>
                                <MenuItem value="after_wipe">После вайпа</MenuItem>
                                <MenuItem value="daily">Ежедневно</MenuItem>
                                <MenuItem value="weekly">Еженедельно</MenuItem>
                                <MenuItem value="once">Один раз</MenuItem>
                                <MenuItem value="cron">Cron-выражение</MenuItem>
                            </Select>
                        </FormControl>

                        {scheduleDialog.draft.mode === 'before_wipe' && (
                            <TextField
                                type="number"
                                label="За сколько дней до вайпа"
                                value={scheduleDialog.draft.offset_days}
                                onChange={(e) => updateScheduleDraft('offset_days', Number(e.target.value))}
                                inputProps={{ min: 0, max: 30 }}
                                fullWidth
                            />
                        )}

                        {['before_wipe', 'after_wipe', 'daily', 'weekly'].includes(scheduleDialog.draft.mode) && (
                            <TextField
                                type="time"
                                label="Время выполнения"
                                value={scheduleDialog.draft.run_at || ''}
                                onChange={(e) => updateScheduleDraft('run_at', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        )}

                        {scheduleDialog.draft.mode === 'weekly' && (
                            <FormControl fullWidth>
                                <InputLabel>День недели</InputLabel>
                                <Select
                                    label="День недели"
                                    value={String(scheduleDialog.draft.weekday ?? '1')}
                                    onChange={(e) => updateScheduleDraft('weekday', e.target.value)}
                                >
                                    <MenuItem value="1">Понедельник</MenuItem>
                                    <MenuItem value="2">Вторник</MenuItem>
                                    <MenuItem value="3">Среда</MenuItem>
                                    <MenuItem value="4">Четверг</MenuItem>
                                    <MenuItem value="5">Пятница</MenuItem>
                                    <MenuItem value="6">Суббота</MenuItem>
                                    <MenuItem value="7">Воскресенье</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        {scheduleDialog.draft.mode === 'once' && (
                            <TextField
                                type="datetime-local"
                                label="Дата и время запуска"
                                value={scheduleDialog.draft.starts_at || ''}
                                onChange={(e) => updateScheduleDraft('starts_at', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        )}

                        {scheduleDialog.draft.mode === 'cron' && (
                            <TextField
                                label="Cron"
                                placeholder="Например: 0 4 * * 1"
                                value={scheduleDialog.draft.cron || ''}
                                onChange={(e) => updateScheduleDraft('cron', e.target.value)}
                                helperText="Формат: минута час день_месяца месяц день_недели"
                                fullWidth
                            />
                        )}

                        <TextField
                            label="Комментарий"
                            value={scheduleDialog.draft.note || ''}
                            onChange={(e) => updateScheduleDraft('note', e.target.value)}
                            placeholder="Например: отключить перед вайпом / включить после вайпа"
                            multiline
                            minRows={2}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={startNewScheduleRule} disabled={savingSchedule}>Очистить форму</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={closeScheduleDialog} disabled={savingSchedule}>Закрыть</Button>
                    <Button variant="contained" onClick={saveSchedule} disabled={savingSchedule}>
                        {scheduleDialog.editingScheduleId ? 'Сохранить правило' : 'Добавить правило'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default ServerPlugins;
